import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiExternalLink, FiZap } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, DataTable, Button, Badge, ConfirmDialog, Toggle, Modal, Input, ImageUpload } from '../../components/common';
import { IMAGE_SIZE_PRESETS } from '../../utils/imageCompressor';
import { clientService } from '../../services/clientService';
import ReviewFormInput from '../reviews/ReviewFormInput';
import { useDebounce, useModal, useForm } from '../../hooks';
import { validators } from '../../utils/validators';
import './ClientsPage.css';

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [totalItems, setTotalItems] = useState(0);
  
  const formModal = useModal();
  const deleteModal = useModal();
  const [saving, setSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  const validationRules = {
    name: [validators.required, validators.minLength(2)],
  };

  const form = useForm({
    name: '',
    logo: null,
    websiteUrl: '',
    client_description: '',
    isActive: true
  }, validationRules);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await clientService.getAll({ search: debouncedSearch });
      setClients(response.data.clients);
      setTotalItems(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleSearch = (value) => {
    setSearch(value);
  };

  const handleOpenForm = (client = null) => {
    if (client) {
      form.setValues({
        id: client.id,
        name: client.name,
        logo: client.logo ? `https://backend.designinfra.co.in/storage/${client.logo}` : null,
        websiteUrl: client.websiteUrl || '',
        client_description: client.client_description || '',
        isActive: client.isActive
      });
    } else {
      form.reset();
    }
    formModal.open(client);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.validate()) {
      toast.error('Please fix the form errors');
      return;
    }
    
    setSaving(true);
    try {
      // Extract the file from the logo object returned by ImageUpload
      const payload = {
        name: form.values.name,
        websiteUrl: form.values.websiteUrl,
        client_description: form.values.client_description,
        isActive: form.values.isActive,
        logo: form.values.logo?.file || form.values.logo
      };
      
      if (formModal.data) {
        await clientService.update(formModal.data.id, payload);
        toast.success('Client updated successfully');
      } else {
        await clientService.create(payload);
        toast.success('Client added successfully');
      }
      
      formModal.close();
      fetchClients();
    } catch (error) {
      toast.error('Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await clientService.toggleStatus(id);
      setClients(prev => prev.map(item => 
        item.id === id ? { ...item, isActive: !item.isActive } : item
      ));
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await clientService.delete(deleteModal.data);
      toast.success('Client deleted successfully');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    } finally {
      setDeleteLoading(false);
      deleteModal.close();
    }
  };

  const fillDemoData = () => {
    form.setValues({
      ...form.values,
      name: 'Demo Client Pvt Ltd',
      websiteUrl: 'https://democlient.com',
      client_description: 'A leading company providing innovative solutions in technology and business consulting.',
      isActive: true,
    });
    toast.info('Demo data filled');
  };

  const columns = [
    {
      header: 'Client',
      render: (row) => (
        <div className="client-cell">
          {row.logo && (
            <img src={`https://backend.designinfra.co.in/storage/${row.logo}`} alt={row.name} className="client-logo" />
          )}
          <span className="client-name">{row.name}</span>
        </div>
      )
    },
    {
      header: 'Website',
      accessor: 'websiteUrl',
      render: (row) => row.websiteUrl ? (
        <a href={row.websiteUrl} target="_blank" rel="noopener noreferrer" className="website-link">
          {row.websiteUrl.replace(/^https?:\/\//, '')}
          <FiExternalLink />
        </a>
      ) : '-'
    },
    {
      header: 'Status',
      width: '100px',
      render: (row) => (
        <Toggle
          name={`status-${row.id}`}
          checked={row.isActive}
          onChange={() => handleToggleStatus(row.id)}
          size="small"
        />
      )
    },
    {
      header: 'State',
      width: '100px',
      render: (row) => (
        <Badge variant={row.isActive ? 'success' : 'warning'}>
          {row.isActive ? 'Active' : 'Inactive'}
        </Badge>
      )
    },
    {
      header: 'Actions',
      width: '100px',
      align: 'center',
      render: (row) => (
        <div className="action-buttons">
          <button 
            className="action-btn edit"
            title="Edit"
            onClick={() => handleOpenForm(row)}
          >
            <FiEdit2 />
          </button>
          <button 
            className="action-btn delete"
            title="Delete"
            onClick={() => deleteModal.open(row.id)}
          >
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="clients-page">
      <Card
        title="Clients"
        subtitle={`${totalItems} clients`}
        action={
          <Button icon={<FiPlus />} onClick={() => handleOpenForm()}>
            Add Client
          </Button>
        }
      >
        <DataTable
          columns={columns}
          data={clients}
          loading={loading}
          searchable
          searchPlaceholder="Search clients..."
          searchValue={search}
          onSearch={handleSearch}
          exportable
          exportFilename="clients-export"
          emptyMessage="No clients found"
        />
      </Card>

      {/* Form Modal */}
      <Modal
        isOpen={formModal.isOpen}
        onClose={formModal.close}
        title={formModal.data ? 'Edit Client' : 'Add Client'}
        size="medium"
        footer={
          <>
            <Button variant="ghost" onClick={formModal.close}>
              Cancel
            </Button>
            <Button variant="secondary" icon={<FiZap />} onClick={fillDemoData} type="button">
              Fill Demo Data
            </Button>
            <Button onClick={handleSubmit} loading={saving}>
              {formModal.data ? 'Update' : 'Save'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="client-form">
          <ReviewFormInput
            className="form-text-input"
            name="name"
            label="Client Name"
            placeholder="Enter client name"
            value={form.values.name}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            error={form.errors.name}
            touched={form.touched.name}
            required
          />
          <ReviewFormInput
            className="form-text-input"
            name="websiteUrl"
            label="Website URL"
            placeholder="https://example.com"
            value={form.values.websiteUrl}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
          />
          <ReviewFormInput
            className="form-text-input"
            name="client_description"
            label="Client Description"
            placeholder="Enter client description"
            value={form.values.client_description}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            textarea
            rows={4}
          />
          <ImageUpload
            value={form.values.logo}
            onChange={(logo) => form.setFieldValue('logo', logo)}
            label="Client Logo"
            {...IMAGE_SIZE_PRESETS.small}
          />
          <Toggle
            name="isActive"
            checked={form.values.isActive}
            onChange={(e) => form.setFieldValue('isActive', e.target.checked)}
            label="Active"
          />
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        title="Delete Client"
        message="Are you sure you want to delete this client? This action cannot be undone."
        confirmText="Delete"
        loading={deleteLoading}
      />
    </div>
  );
};

export default ClientsPage;
